import { useContext, useState } from "react";
import {
  Box,
  Button,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/material";
import { spamCheck } from "screens/Campaigns/CampaignService";
import ReplayIcon from "@mui/icons-material/Replay";
import { handleErrorMessage } from "helpers/methods";
import { CampaignContext } from "../../Context";

interface CustomProps {
  exportPlainText: Function;
}

const SpamCheck = (props: CustomProps) => {
  const { submitCampaign, setSubmitCampaign } = useContext(CampaignContext);
  const [spamWords, setSpamWords] = useState<string[]>([]);
  const [reRun, setReRun] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  const runSpamCheck = async () => {
    try {
      setLoading(true);
      const content = await props.exportPlainText();
      const aiResponse = await spamCheck(content.text);
      const campaign = {
        ...submitCampaign,
        campaignData: {
          ...submitCampaign.campaignData,
          phishingCheck: {
            spamPrediction: aiResponse.spamPrediction,
            spam: aiResponse.spam,
            phishing: aiResponse.phishing,
            phishingPercentage: aiResponse.phishingPercentage
          },
        },
      };
      setSubmitCampaign(campaign);
      setSpamWords(aiResponse.topSpamKeywords);
      setReRun(true);
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      handleErrorMessage(error);
    }
  };

  const getBackgroundColor = (percentage: any) => {
    if (percentage >= 1 && percentage <= 50) return "#6BC6261A";
    if (percentage > 50 && percentage <= 70) return "#F1C1001A";
    if (percentage > 70 && percentage <= 100) return "#EA32211C";
  };

  const getTextColor = (percentage: any) => {
    if (percentage >= 1 && percentage <= 50) return "#6BC626";
    if (percentage > 50 && percentage <= 70) return "#F1C100";
    if (percentage > 70 && percentage <= 100) return "#EA3221";
    return "#000";
  };

  return (
    <Stack spacing={2}>
      <Stack spacing={1}>
        <Typography variant="h6">Spam Check</Typography>
        <Typography variant="body2">
          Spam Word Detection scans your email for terms flagged as spam.
          Removing them improves deliverability and avoids the spam folder.
        </Typography>
      </Stack>

      {reRun ? (
        loading ? (
          <Stack spacing={1}>
            <Skeleton variant="text" width="80px" height={20} />
            <Skeleton variant="rounded" width="100%" height={50} />
          </Stack>
        ) : (
          <>
            <Typography variant="subtitle1" color="#475569">
              Spam Words
            </Typography>
            <Box
              sx={{
                border: "1px solid #47556940",
                borderRadius: "8px",
                width: "auto",
                backgroundColor: "#FFFFFF",
                padding: 2,
              }}
              py={3}
            >
              {Object.keys(spamWords).length > 0 ? (
                <Grid container spacing={1}>
                  {Object.entries(spamWords).map(([word, percentage]) => (
                    <Grid item key={word}>
                      <Box
                        width="auto"
                        padding={1}
                        sx={{
                          backgroundColor: getBackgroundColor(percentage),
                          borderRadius: "4px",
                          display: "inline-block",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: getTextColor(percentage),
                          }}
                        >
                          {word}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Stack
                  direction="column"
                  justifyContent="center"
                  alignItems="center"
                  height="100%"
                >
                  <Typography>😃</Typography>
                  <Typography variant="subtitle2">
                    No spam words found!
                  </Typography>
                  <Typography variant="body2">
                    Your content is fully optimized.
                  </Typography>
                </Stack>
              )}
            </Box>
          </>
        )
      ) : (
        <Button
          variant="contained"
          size="small"
          disabled={loading}
          onClick={runSpamCheck}
        >
          Run Spam Check
        </Button>
      )}

      {reRun && (
        <Box>
          <IconButton
            sx={{ border: "1px solid #7551D0" }}
            onClick={runSpamCheck}
            disabled={loading}
          >
            <SvgIcon>
              <ReplayIcon htmlColor="#7551D0" />
            </SvgIcon>
          </IconButton>
        </Box>
      )}
    </Stack>
  );
};

export default SpamCheck;
